import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  GoogleAdsIntegrationListItem,
  GoogleAdsIntegrationStatus,
} from "../../../types/googleAds.types";

interface GoogleAdsState {
  integrations: GoogleAdsIntegrationListItem[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  limit: number;
  statusFilter: GoogleAdsIntegrationStatus | null;
}

const initialState: GoogleAdsState = {
  integrations: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  limit: 20,
  statusFilter: null,
};

const googleAdsSlice = createSlice({
  name: "googleAds",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setIntegrations: (
      state,
      action: PayloadAction<{
        integrations: GoogleAdsIntegrationListItem[];
        total: number;
        page: number;
        limit: number;
      }>
    ) => {
      state.integrations = action.payload.integrations;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.loading = false;
      state.error = null;
    },
    setStatusFilter: (state, action: PayloadAction<GoogleAdsIntegrationStatus | null>) => {
      state.statusFilter = action.payload;
      state.page = 1; // Reset to first page when filtering
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
      state.page = 1;
    },
    updateIntegrationInList: (state, action: PayloadAction<GoogleAdsIntegrationListItem>) => {
      const index = state.integrations.findIndex(
        (integration) => integration._id === action.payload._id
      );
      if (index !== -1) {
        state.integrations[index] = action.payload;
      }
    },
    removeIntegrationFromList: (state, action: PayloadAction<string>) => {
      state.integrations = state.integrations.filter((integration) => integration._id !== action.payload);
      state.total = Math.max(0, state.total - 1);
    },
    addIntegrationToList: (state, action: PayloadAction<GoogleAdsIntegrationListItem>) => {
      state.integrations.unshift(action.payload);
      state.total += 1;
    },
    clearIntegrations: (state) => {
      state.integrations = [];
      state.total = 0;
      state.page = 1;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setIntegrations,
  setStatusFilter,
  setPage,
  setLimit,
  updateIntegrationInList,
  removeIntegrationFromList,
  addIntegrationToList,
  clearIntegrations,
} = googleAdsSlice.actions;

export default googleAdsSlice.reducer;
